import { addSeconds, differenceInSeconds, format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import Alert from '~/components/Alert';
import { CardBox } from '~/components/ui/Card';
import Dialog from '~/components/ui/Dialog';
import SettingsRow from '~/components/ui/SettingsRow';
import { SkeletonCard } from '~/components/ui/Skeleton';
import { useBranding } from '~/lib/branding';
import useGenerateQrCode from '~/modules/qr/useGenerateQrCode';

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const time = addSeconds(new Date(0), seconds);
  if (Number.isNaN(time.getTime())) return '00:00';
  return format(time, 'mm:ss');
};

export default function EventPass() {
  const { t } = useTranslation();
  const { data: qrCode, isLoading, isError, refetch } = useGenerateQrCode();
  const [now, setNow] = useState(() => new Date());
  const branding = useBranding();

  const expiresAtDate =
    qrCode && typeof qrCode.expires_at === 'number' && !Number.isNaN(qrCode.expires_at)
      ? new Date(qrCode.expires_at * 1000)
      : null;

  const secondsLeft =
    expiresAtDate && !Number.isNaN(expiresAtDate.getTime())
      ? Math.max(differenceInSeconds(expiresAtDate, now), 0)
      : 0;

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setNow(new Date());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (qrCode?.expires_at) {
      setNow(new Date());
    }
  }, [qrCode?.expires_at]);

  useEffect(() => {
    if (secondsLeft === 0 && qrCode) {
      refetch();
    }
  }, [secondsLeft, qrCode, refetch]);

  return (
    <>
      {isLoading ? (
        <SkeletonCard height={94} />
      ) : (
        <div>
          <p className="text-sm font-semibold text-section-title mb-3">{t('eventPass.title')}</p>
          <CardBox className="p-0">
            <DialogTrigger>
              <SettingsRow
                icon="qr_code"
                label={t('eventPass.subtitle')}
                value={t('eventPass.description')}
              >
                <span className="shrink-0 whitespace-nowrap text-[13px] font-semibold text-accent compact:hidden">
                  {t('eventPass.showQrCode')}
                </span>
              </SettingsRow>
              <Dialog className="flex flex-col gap-4 sm:max-w-115">
                {isError ? (
                  <Alert variant="error" description={t('eventPass.error')} />
                ) : (
                  <div className="flex flex-col items-center text-center w-full">
                    <h2 className="text-[27px] leading-[1.2] tracking-[-0.6px] font-semibold">
                      {t('eventPass.modalTitle')}
                    </h2>
                    <div className="rounded-xl p-4 bg-sky-blue mt-6 w-full aspect-square compact:max-w-55 compact:mt-4">
                      <QRCodeSVG
                        value={qrCode?.qr_data as string}
                        bgColor={branding === 'hero' ? '#D7EDFE' : '#fff'}
                        fgColor="#000000"
                        className="w-full h-full"
                      />
                    </div>
                    <p className="text-[19px] mt-6 font-medium">{formatTime(secondsLeft)}</p>
                    <p className="text-gray-3 mt-2 text-[14px] leading-[1.8]">
                      {t('eventPass.modalDescription')}
                    </p>
                  </div>
                )}
              </Dialog>
            </DialogTrigger>
          </CardBox>
        </div>
      )}
    </>
  );
}
